import { match } from "ts-pattern";

import { IncidentWorkItemStatusSchema } from "@thalamos/common";
import type { IncidentWorkItemWithContext } from "@thalamos/vision-common";
import {
  calculateIncidentWorkItemStatus,
  getCommandUnit,
  getIncidentTypeSelection,
  getPatientName,
  getWorkflowTranslationContext,
  IncidentTypeSchema,
} from "@thalamos/vision-common";

import { renderUkDateTime } from "../../util.js";
import { calculateDerivedIncidentStatus } from "./calculateDerivedIncidentStatus.js";

export type IncidentDisplayValues = {
  typeOfIncident: string;
  createdAt: string;
  nameOfPerson: string;
  commandUnit: string;
  currentAssignee: string;
};

/**
 * Extracts pre-computed display values from an IncidentWorkItemWithContext.
 *
 * This is shared between the desktop table view and the mobile card view
 * to avoid duplicating the derivation logic.
 */
export function extractIncidentDisplayValues(
  incidentWorkItem: IncidentWorkItemWithContext,
  t: (key: string, options?: Record<string, unknown>) => string,
): IncidentDisplayValues {
  const { incidentWorkItem: inner, participants } = incidentWorkItem;

  const owner = participants.find(
    (participant) => participant.role === "owner",
  );
  const ownerName = owner ? owner.name : t("incidentWorkItem.unknownOwner");

  const commandUnitResult = getCommandUnit({ incidentWorkItem });
  const { fullName } = getPatientName({ incidentWorkItem });

  const incidentTypeSelection = getIncidentTypeSelection({
    incidentWorkItem,
  }).unwrapOr(undefined);

  const typeOfIncidentForm = match(incidentTypeSelection)
    .with(
      IncidentTypeSchema.enum.section136,
      () => IncidentTypeSchema.enum.section136,
    )
    .with(
      IncidentTypeSchema.enum.mentalCapacityAct,
      () => IncidentTypeSchema.enum.mentalCapacityAct,
    )
    .with(
      IncidentTypeSchema.enum.voluntary,
      () => IncidentTypeSchema.enum.voluntary,
    )
    .with(IncidentTypeSchema.enum.other, () => IncidentTypeSchema.enum.other)
    .with(undefined, () => IncidentTypeSchema.enum.section136)
    .exhaustive();

  const created = renderUkDateTime(inner.createdAt, true);

  const derivedIncidentStatus =
    calculateDerivedIncidentStatus(incidentWorkItem);

  const workflowContext = getWorkflowTranslationContext({ incidentWorkItem });
  const tOptions = { context: workflowContext };

  const typeOfIncident =
    calculateIncidentWorkItemStatus(incidentWorkItem).unwrapOr(
      IncidentWorkItemStatusSchema.enum.unknown,
    ) === IncidentWorkItemStatusSchema.enum.unknown
      ? t(`incidentWorkItem.${derivedIncidentStatus}`, tOptions)
      : `${t(`incidentWorkItem.type.${typeOfIncidentForm}`)} - ${t(`incidentWorkItem.${derivedIncidentStatus}`, tOptions)}`;

  return {
    typeOfIncident,
    createdAt: created,
    nameOfPerson: fullName,
    commandUnit: commandUnitResult?.name ?? "Unknown",
    currentAssignee: ownerName,
  };
}
